import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const EMPTY = { nombre: '', apellidos: '', username: '', email: '', password: '', rol: 'trabajador', activo: 1 };

export default function GestionTrabajadores() {
  const [trabajadores, setTrabajadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = oculto, {} = nuevo, {id,...} = editar
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filtro, setFiltro] = useState('todos'); // todos | activos | inactivos
  const [borrando, setBorrando] = useState(null); // id del trabajador que se está eliminando

  const fetchTrabajadores = async () => {
    try {
      const res = await api.get('/trabajadores');
      setTrabajadores(res.data.trabajadores);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTrabajadores(); }, []);

  const showMsg = (tipo, texto) => {
    setMsg({ tipo, texto });
    setTimeout(() => setMsg(null), 4000);
  };

  const openNew = () => setForm({ ...EMPTY });
  const openEdit = (t) => setForm({ ...t, password: '' });
  const closeForm = () => setForm(null);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/trabajadores/${form.id}`, form);
        showMsg('ok', 'Trabajador actualizado correctamente');
      } else {
        await api.post('/trabajadores', form);
        showMsg('ok', 'Trabajador creado correctamente');
      }
      await fetchTrabajadores();
      closeForm();
    } catch (err) {
      showMsg('err', err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (t) => {
    try {
      await api.put(`/trabajadores/${t.id}`, { activo: t.activo ? 0 : 1 });
      showMsg('ok', t.activo ? 'Trabajador desactivado' : 'Trabajador reactivado');
      await fetchTrabajadores();
    } catch (err) {
      showMsg('err', err.response?.data?.error || 'Error');
    }
  };

  const handleDelete = async (t) => {
    const confirmado = window.confirm(
      `Esto eliminará PERMANENTEMENTE a ${t.nombre} ${t.apellidos} y todos sus fichajes. Esta acción no se puede deshacer.\n\n¿Seguro que quieres continuar?`
    );
    if (!confirmado) return;

    setBorrando(t.id);
    try {
      const res = await api.delete(`/trabajadores/${t.id}`);
      const n = res.data?.fichajesEliminados;
      showMsg('ok', n ? `Trabajador eliminado (${n} fichajes borrados)` : 'Trabajador eliminado');
      await fetchTrabajadores();
    } catch (err) {
      showMsg('err', err.response?.data?.error || 'Error al eliminar');
    } finally {
      setBorrando(null);
    }
  };

  const filtrados = trabajadores.filter(t => {
    if (filtro === 'activos') return t.activo;
    if (filtro === 'inactivos') return !t.activo;
    return true;
  });

  if (loading) return <div className="text-center py-12 text-gray-400">Cargando...</div>;

  return (
    <div className="space-y-4">
      {/* Acciones */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2">
          {['todos', 'activos', 'inactivos'].map(f => (
            <button
              key={f}
              onClick={() => setFiltro(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                ${filtro === f ? 'bg-mgh-blue-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <button onClick={openNew} className="btn-primary flex items-center gap-2">
          <span>+</span> Nuevo Trabajador
        </button>
      </div>

      {/* Mensaje */}
      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm font-medium
          ${msg.tipo === 'ok' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
          {msg.texto}
        </div>
      )}

      {/* Modal / Formulario */}
      {form && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="font-bold text-gray-800 text-lg">
                {form.id ? 'Editar Trabajador' : 'Nuevo Trabajador'}
              </h3>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Nombre *</label>
                  <input className="input" required value={form.nombre}
                    onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Apellidos *</label>
                  <input className="input" required value={form.apellidos}
                    onChange={e => setForm(f => ({ ...f, apellidos: e.target.value }))} />
                </div>
              </div>

              <div>
                <label className="label">Usuario *</label>
                <input className="input" required value={form.username}
                  disabled={!!form.id}
                  placeholder="nombre.apellido"
                  onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase() }))} />
                {form.id && <p className="text-xs text-gray-400 mt-1">El usuario no se puede cambiar</p>}
              </div>

              <div>
                <label className="label">Email</label>
                <input type="email" className="input" value={form.email || ''}
                  placeholder="correo@mghelectricidad.es"
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>

              <div>
                <label className="label">{form.id ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</label>
                <input type="password" className="input" value={form.password || ''}
                  required={!form.id} minLength={6}
                  placeholder={form.id ? 'Dejar vacío para mantener' : 'Mínimo 6 caracteres'}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>

              <div>
                <label className="label">Rol</label>
                <select className="input" value={form.rol}
                  onChange={e => setForm(f => ({ ...f, rol: e.target.value }))}>
                  <option value="trabajador">Trabajador</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              {form.id && (
                <div className="flex items-center gap-3">
                  <label className="label mb-0">Estado</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!form.activo}
                      onChange={e => setForm(f => ({ ...f, activo: e.target.checked ? 1 : 0 }))} />
                    <span className="text-sm">{form.activo ? 'Activo' : 'Inactivo'}</span>
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} className="btn-secondary flex-1">
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Trabajador</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden sm:table-cell">Usuario</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">Email</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Rol</th>
                <th className="text-center px-4 py-3 font-semibold text-gray-600">Estado</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((t, i) => (
                <tr key={t.id} className={`border-b border-gray-100 hover:bg-gray-50 ${!t.activo ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{t.nombre} {t.apellidos}</div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-gray-500 font-mono text-xs">
                    {t.username}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-gray-500 text-xs">
                    {t.email || '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                      ${t.rol === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                      {t.rol}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium
                      ${t.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {t.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => openEdit(t)}
                        className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors font-medium">
                        Editar
                      </button>
                      <button onClick={() => toggleActivo(t)}
                        className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors
                          ${t.activo ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
                        {t.activo ? 'Desactivar' : 'Activar'}
                      </button>
                      {t.rol !== 'admin' && (
                        <button onClick={() => handleDelete(t)}
                          disabled={borrando === t.id}
                          title="Eliminar permanentemente (borra también sus fichajes)"
                          className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-red-600 hover:text-white transition-colors font-medium disabled:opacity-50">
                          {borrando === t.id ? 'Eliminando...' : 'Eliminar'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtrados.length === 0 && (
            <div className="text-center py-8 text-gray-400">No hay trabajadores</div>
          )}
        </div>
      </div>
    </div>
  );
}
